import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Crown,
  DollarSign,
  HelpCircle,
  Home,
  Mail,
  MessageSquare,
  RefreshCw,
  Shield
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#f97316';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#f97316';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <Home className="w-5 h-5" />,
      question: "How does Gauge build an estimate?",
      answer: (
        <div className="space-y-2">
          <p>Pick a project type, enter dimensions, and choose a quality tier. Gauge multiplies your inputs against its built-in cost library and produces an itemized estimate — materials, labor, and totals — instantly, on your device.</p>
        </div>
      )
    },
    {
      icon: <DollarSign className="w-5 h-5" />,
      question: "How accurate are the estimates?",
      answer: (
        <div className="space-y-2">
          <p>They are planning figures, not quotes. Real costs vary by region, season, and site conditions — sometimes significantly.</p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Use Gauge to set a ballpark and compare options, then get real quotes from contractors before committing.
          </p>
        </div>
      )
    },
    {
      icon: <Shield className="w-5 h-5" />,
      question: "Does Gauge send my project details anywhere?",
      answer: (
        <div className="space-y-2">
          <p>No. The cost library ships inside the app and estimates are computed on-device. Gauge has no account, no analytics, and makes no network requests.</p>
        </div>
      )
    },
    {
      icon: <RefreshCw className="w-5 h-5" />,
      question: "Can I compare different versions of a project?",
      answer: (
        <div className="space-y-2">
          <p>Yes — save a project, duplicate or adjust its tier and dimensions, and compare the resulting estimates side by side to see where the money actually goes.</p>
        </div>
      )
    },
    {
      icon: <Crown className="w-5 h-5" />,
      question: "What is Gauge Pro and how do I restore it?",
      answer: (
        <div className="space-y-2">
          <p>Gauge Pro is an optional monthly or yearly subscription that unlocks additional project types and estimating features. To restore a previous purchase:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open Settings inside Gauge</li>
            <li>Tap Restore Purchases</li>
            <li>Sign in with the Apple ID you used to purchase</li>
          </ol>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="Gauge" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=Gauge%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve Gauge
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Gauge</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlabs</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 17+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Utilities</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/gauge/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/gauge/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms &amp; Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
